import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { appelApi } from './api';
import type { Nps } from './useQuestionnaires';
import type { VueSynthese } from './useSynthesesMensuelles';

export type ChoixPeriode = 'MOIS' | 'MOIS_PRECEDENT' | 'TRIMESTRE' | 'ANNEE' | 'LIBRE';

export interface Montant {
  texte: string;
  centimes: number;
}

export interface DonneesDirection {
  calculeLe: string;
  periode: { choix: ChoixPeriode; du: string; au: string; libelle: string };
  filtres: {
    periodes: { valeur: ChoixPeriode; libelle: string }[];
    formations: { code: string; titre: string }[];
    promotions: { cle: string; libelle: string; siteCode: string }[];
    formation: string | null;
    promotion: string | null;
  };
  /** En formation sur la période ; `aujourdHui`, l'effectif du jour. */
  actifs: { nombre: number; aujourdHui: number; entres: number; detail: string };
  heures: { minutes: number; texte: string; demiJournees: number; tauxPresence: number | null; detail: string };
  chiffreAffaires: {
    netHt: Montant;
    facturesHt: Montant;
    avoirsHt: Montant;
    factures: number;
    avoirs: number;
    encaisse: Montant;
    reglements: number;
    resteDu: Montant & { factures: number };
    detail: string;
  };
  satisfaction: {
    reponses: number;
    moyenne: number | null;
    notes: number;
    recommandation: { repondants: number; oui: number; part: number | null };
    nps: Nps;
    valeur: string;
    detail: string;
  };
  qualiopi: {
    etat: 'CERTIFIE' | 'A_VERIFIER' | 'NON_RELEVE';
    libelle: string;
    ton: string;
    organismes: {
      numeroDeclaration: string | null;
      antennes: string[];
      certifie: boolean;
      categories: { libelle: string; certifiee: boolean }[];
      releveLe: string | null;
      detail: string;
    }[];
    echeance: { intitule: string; nature: string; date: string; jours: number; libelle: string; ton: string } | null;
    evolutionsAAnalyser: number;
    /** Les indicateurs de l'espace de conformité (TF-431), 33 depuis TF-471. */
    conformite: { indicateurs: number; applicables: number; conformes: number; aCompleter: number; nonApplicables: number };
  };
  pipeline: { id: string; libelle: string; valeur: number; detail: string; ton: string; page: string }[];
  antennes: {
    code: string;
    nom: string;
    actifs: number;
    entres: number;
    heures: string;
    tauxPresence: number | null;
    chiffreAffaires: Montant;
  }[];
  /** La synthèse mensuelle la plus récente (TF-440), `null` avant la première. */
  derniereSynthese: VueSynthese | null;
}

export interface FiltreDirection {
  site: string;
  periode: ChoixPeriode;
  du: string;
  au: string;
  formation: string;
  promotion: string;
}

/** Les chiffres se relisent seuls à cet intervalle, et au retour sur l'onglet. */
export const RAFRAICHISSEMENT_DIRECTION_MS = 60_000;

/**
 * Tableau de bord de la direction (TF-425) : période, antenne, formation et
 * promotion vont à l'API, qui résout la période et relit la base à chaque appel.
 * Une période libre incomplète n'est pas demandée.
 */
export function useDashboardDirection(filtre: FiltreDirection) {
  const parametres = new URLSearchParams();
  if (filtre.site && filtre.site !== 'TOUS') parametres.set('site', filtre.site);
  parametres.set('periode', filtre.periode);
  if (filtre.periode === 'LIBRE') {
    parametres.set('du', filtre.du);
    parametres.set('au', filtre.au);
  }
  if (filtre.formation) parametres.set('formation', filtre.formation);
  if (filtre.promotion) parametres.set('promotion', filtre.promotion);
  const chaine = parametres.toString();
  const libreIncomplete = filtre.periode === 'LIBRE' && (!filtre.du || !filtre.au || filtre.du > filtre.au);

  return useQuery({
    // Sous « financements » : une facture émise ou un règlement saisi l'actualisent aussi.
    queryKey: ['financements', 'tableau-de-bord-direction', chaine],
    queryFn: () => appelApi<DonneesDirection>(`/dashboard/direction?${chaine}`),
    enabled: !libreIncomplete,
    placeholderData: keepPreviousData,
    refetchInterval: RAFRAICHISSEMENT_DIRECTION_MS,
    refetchOnWindowFocus: true,
  });
}
